import React, { useEffect, useState } from "react";
import { TreeAPI } from "../services/api";
import { ChevronDown, ChevronRight, Workflow } from "lucide-react";

interface TreeNode {
  text: string;
  tag: number;
  color?: string;
  children: TreeNode[];

  wbsNo?: number;
  notationNo?: number;

  notationHead?: number;
  sequence?: number;
  front?: number;
}

interface Props {
  projectId: number;
  refresh?: number;
  onSelect?: (node: TreeNode) => void;
}

const getNodeKey = (node: TreeNode) => {
  if (node.tag === 0 && !node.wbsNo) {
    return "root";
  }

  return `wbs-${node.wbsNo ?? node.tag}`;
};

const findNode = (
  node: TreeNode,
  key: string
): TreeNode | null => {
  if (getNodeKey(node) === key) return node;

  for (const child of node.children || []) {
    const found = findNode(child, key);
    if (found) return found;
  }

  return null;
};

const getNodeColor = () => "text-black";

/* const getIcon = (isSelected: boolean) => (
  <Workflow
    size={16}
    className={isSelected ? "text-indigo-700" : "text-indigo-500"}
  />
); */

/* -------------------------------------------------------
   Remove all notation nodes recursively
--------------------------------------------------------*/
const filterWBSTree = (node: TreeNode): TreeNode | null => {
  // Skip notation nodes
  if (node.tag < 0) {
    return null;
  }

  return {
    ...node,
    children: (node.children || [])
      .map(filterWBSTree)
      .filter((x): x is TreeNode => x !== null),
  };
};

interface NodeProps {
  node: TreeNode;
  level?: number;
  selectedTag: number | null;
  onSelect?: (node: TreeNode) => void;
  openNodes: Record<string, boolean>;
  setOpenNodes: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

const AccordionNode = ({
  node,
  level = 0,
  selectedTag,
  onSelect,
  openNodes,
  setOpenNodes,
}: NodeProps) => {
  const hasChildren = node.children?.length > 0;

  const isSelected = selectedTag === node.tag;

  const nodeKey = getNodeKey(node);

  const open = !!openNodes[nodeKey];

  return (
    <div style={{ paddingLeft: `${level * 10}px` }}>
      <div
        className={`flex items-center rounded-md px-1 py-0.5 cursor-pointer ${
          isSelected
            ? "bg-indigo-100 text-indigo-700"
            : "hover:bg-indigo-50"
        }`}
        onClick={() => {
          if (hasChildren) {
            setOpenNodes((prev) => ({
              ...prev,
              [nodeKey]: !prev[nodeKey],
            }));
          }

          onSelect?.(node);
        }}
      >
        {hasChildren ? (
          <button
            className="w-4 h-4 flex items-center justify-center shrink-0"
            onClick={(e) => {
              e.stopPropagation();

              setOpenNodes((prev) => ({
                ...prev,
                [nodeKey]: !prev[nodeKey],
              }));
            }}
          >
            {open ? (
              <ChevronDown size={14} className="text-gray-600" />
            ) : (
              <ChevronRight size={14} className="text-gray-600" />
            )}
          </button>
        ) : (
          <span className="w-4 h-4" />
        )}

        <div className="flex items-center gap-1 min-w-0">
          {/* {getIcon(isSelected)} */}

          <span
            className={`${getNodeColor()} whitespace-nowrap overflow-hidden text-ellipsis`}
            style={{
              fontFamily: "'Segoe UI'",
              fontSize: "13px",
              fontWeight: 400,
            }}
          >
            {node.text}
          </span>

          <span
            className="text-gray-500"
            style={{
              fontFamily: "'Segoe UI'",
              fontSize: "13px",
              fontWeight: 400,
            }}
          >
            {/* WBS : {node.wbsNo} */}
          </span>
        </div>
      </div>

      {/* {open &&
        hasChildren &&
        node.children.map((child) => (   
            key={getNodeKey(child)}
            node={child}
            level={level + 1}
            selectedTag={selectedTag}
            onSelect={onSelect}
            openNodes={openNodes}
            setOpenNodes={setOpenNodes}
          />
        ))} */}
        {open && hasChildren && (
  <div>
    {[...(node.children || [])]
      .sort((a, b) => {
        const textA = String(a.text || "").trim();
        const textB = String(b.text || "").trim();

        const numberA = textA.match(/^\d+/);
        const numberB = textB.match(/^\d+/);

        // 1. Both start with numbers → numeric ascending order
        if (numberA && numberB) {
          return Number(numberA[0]) - Number(numberB[0]);
        }

        // 2. Numbered nodes come before alphabetic nodes
        if (numberA && !numberB) {
          return -1;
        }

        if (!numberA && numberB) {
          return 1;
        }

        // 3. Alphabetical order
        return textA.localeCompare(textB, undefined, {
          sensitivity: "base",
          numeric: true,
        });
      })
      .map((child) => (
        <AccordionNode
          key={getNodeKey(child)}
          node={child}
          level={level + 1}
          selectedTag={selectedTag}
          onSelect={onSelect}
          openNodes={openNodes}
          setOpenNodes={setOpenNodes}
        />
      ))}
  </div>
)}
    </div>
  );
};

const FourthTree = ({
  projectId,
  refresh,
  onSelect,
}: Props) => {
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState<number | null>(null);

  const [selectedNodeKey, setSelectedNodeKey] =
  useState<string | null>(null);

  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({});

  const loadTree = () => {
    setLoading(true);

    TreeAPI.getTree(projectId)
      .then((data) => {
        const filteredTree = filterWBSTree(data);
        setTree(filteredTree);
      })
      .catch(() => {
        alert("Failed to load tree");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadTree();
  }, [projectId, refresh]);

  /* useEffect(() => {
    if (!tree) return;

    const rootKey = getNodeKey(tree);

    setOpenNodes({
      [rootKey]: true,
    });

    setSelectedTag(tree.tag);

    onSelect?.(tree);
  }, [tree]); */

  useEffect(() => {
  if (!tree) return;

  const rootKey = getNodeKey(tree);

  setOpenNodes((prev) => ({
    ...prev,
    [rootKey]: true,
  }));

  const savedKey =
    selectedNodeKey ??
    sessionStorage.getItem(`fourthTreeSelection-${projectId}`);

  if (savedKey) {
    const node = findNode(tree, savedKey);

    if (node) {
      setSelectedTag(node.tag);
      setSelectedNodeKey(savedKey);
      onSelect?.(node);
      return;
    }
  }

  setSelectedTag(tree.tag);
  onSelect?.(tree);

}, [tree, selectedNodeKey, projectId]);

  /* const handleSelect = (node: TreeNode) => {
    console.log("FourthTree Selected:", node);
    setSelectedTag(node.tag);
    onSelect?.(node);
  }; */
  const handleSelect = (node: TreeNode) => {
  console.log("FourthTree Selected:", node);

  setSelectedTag(node.tag);

  const key = getNodeKey(node);

  setSelectedNodeKey(key);

  sessionStorage.setItem(
    `fourthTreeSelection-${projectId}`,
    key
  );

  onSelect?.(node);
};

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "'Segoe UI'",
          fontSize: "13px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      className="thin-scrollbar h-full w-full overflow-y-auto bg-transparent p-1"
      style={{
        fontFamily: "'Segoe UI'",
        fontSize: "13px",
        fontWeight: 400,
      }}
    >
      {tree && (
        <AccordionNode
          node={tree}
          selectedTag={selectedTag}
          onSelect={handleSelect}
          openNodes={openNodes}
          setOpenNodes={setOpenNodes}
        />
      )}
    </div>
  );
};

export default FourthTree;